'use client'

import { useSyncExternalStore } from 'react'
import { getActiveRoleOverride, ACTIVE_ROLE_EVENT } from '@/lib/active-role'

/**
 * The per-tab role override, for RENDER paths only.
 *
 * The override lives in sessionStorage, which the server cannot see — so the
 * SSR HTML is always computed with the user's REAL property role.
 *
 * useSyncExternalStore is the sanctioned bridge: the server snapshot is null
 * (matching the SSR HTML), and when the client snapshot differs React
 * re-renders SYNCHRONOUSLY during hydration — before first paint — so an
 * acting-as user never sees a flash of their real role's UI (the old
 * useState+useEffect version applied the override a paint later, which showed
 * the full admin menu for a beat before the acted-as menu replaced it).
 *
 * Requests are unaffected — the Apollo links read lib/active-role directly
 * (not a render path), so every request carries the override from the very
 * first one.
 */
function subscribe(onStoreChange: () => void): () => void {
  window.addEventListener(ACTIVE_ROLE_EVENT, onStoreChange)
  return () => window.removeEventListener(ACTIVE_ROLE_EVENT, onStoreChange)
}

export function useActiveRoleOverride(): string | null {
  return useSyncExternalStore(subscribe, getActiveRoleOverride, () => null)
}
